import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useClientAssociatedProfiles } from './useClientAssociatedProfiles';
import { isOrionClientSelector } from '../../../../CommonSelectors/CommonAreaSelector';
import { Loading } from '../../../../CommonComponents/OPL/Loading/Loading';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { ListItem } from '../../../../CommonComponents/OPL/List/ListItem';
import { showError } from '../../../../CommonComponents/ToastMessenger/ToastMessengerActions';
import { MESSAGES } from '../../../../CommonConstants';
import { useClientAssociatedProfilesMutation } from '../../../api/useClientAssociatedProfilesMutation';
import { MenuHeader, MenuItem, MenuSeparator } from '../../../../CommonComponents/OPL/Menu/Menu';
import { identifyFullstoryUser } from '../../../../CommonUtils/initFullstory';

interface Props {
  clientId: string;
  onSwitchProfile(): void;
  onSwitchProfileError(): void;
}

export const ClientAssociatedProfiles: React.FC<Props> = ({
  clientId,
  onSwitchProfile,
  onSwitchProfileError,
}) => {
  const isOrionClient = useSelector(isOrionClientSelector);

  const { data, isFetching, isError } = useClientAssociatedProfiles({ clientId, isOrionClient });

  const { mutateAsync: switchProfile } = useClientAssociatedProfilesMutation();

  const dispatch = useDispatch();

  const onProfileClick = async item => {
    if (item.selected) {
      return;
    }

    try {
      // Redirect will clear the state.
      onSwitchProfile();

      const { redirectUrl } = await switchProfile({
        userDetailId: item.id,
        clientId,
      });

      identifyFullstoryUser(clientId, { householdId: item.id });

      window.location.href = redirectUrl;
    } catch {
      onSwitchProfileError();

      dispatch(showError(new Error(MESSAGES.GENERIC_ERROR)));
    }
  };

  if (isFetching) {
    return (
      <Box py="16" justifyContent="center" display="flex">
        <Loading size="small" />
      </Box>
    );
  }

  if (isError) {
    return null;
  }

  if (!data || data.length <= 1) {
    return null;
  }

  return (
    <>
      <MenuHeader>Switch households</MenuHeader>

      {data.map(item => (
        <MenuItem key={item.id} onSelect={() => onProfileClick(item)}>
          <ListItem icon={item.icon} variant={item.selected ? 'selected' : 'inactive'}>
            {item.text}
          </ListItem>
        </MenuItem>
      ))}

      <MenuSeparator />
    </>
  );
};
